import { BadRequestException, ForbiddenException, Injectable } from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import {
  PlanTier,
  effectivePlanQuotaBytes,
  resolvePlanTier,
} from '../commerce-admin/plan-quota.constants';
import {
  ALLOCATION_MODES,
  AllocationMode,
  AllocationSummary,
  resolveAllocations,
  validateAllocation,
} from './storage-allocation.util';
import { countsAgainstQuota } from './platform-storage.constants';
import { StorageAdapterResolver } from './storage-adapter-resolver.service';

export interface StorageUsage {
  usedBytes: number;
  capBytes: number;
  availableBytes: number;
  usagePercent: number;
  assetCount: number;
  planTier: PlanTier;
  /** False for BYOS: the merchant pays their own provider, so we track only. */
  quotaEnforced: boolean;
  isHardBlocked: boolean;
  warningLevel: 'none' | 'warning' | 'high' | 'exceeded';
  /** Past the plan allowance but still able to upload — the red-banner state. */
  isOverLimit: boolean;
  /** A platform admin has confirmed this account is entitled to its plan. */
  planVerified: boolean;
  /** Premium, unverified, and therefore on the provisional allowance. */
  awaitingPlanVerification: boolean;
  /**
   * Bytes held by deleted-but-not-yet-purged files.
   *
   * On the payload because it changes what a blocked merchant should be told:
   * "delete something" is useless advice to someone whose quota is already
   * mostly deleted files. Emptying the trash is the action that helps, and
   * they cannot choose it if nothing mentions it.
   */
  trashedBytes: number;
  /** The byte count at which uploads stop. Above the cap; see HARD_BLOCK_MULTIPLIER. */
  blockAtBytes: number;
}

/**
 * Asset states that occupy real storage, and therefore real money.
 *
 *   reserved     — an upload in flight; the bytes are claimed
 *   active       — live media
 *   trashed      — deleted by the merchant, still recoverable, still stored
 *   purge_failed — we tried to delete it and could not; the object is still there
 *
 * `purge_failed` was missing, which meant a failed delete quietly stopped
 * counting at the next recompute while the file stayed in the bucket and stayed
 * on the bill. A byte we are paying for counts, regardless of how we feel about
 * why it is still there.
 */
export const COUNTED_STATUSES: string[] = ['reserved', 'active', 'trashed', 'purge_failed'];

export const WARNING_THRESHOLD = Number(process.env.STORAGE_WARN_PERCENT ?? 80);
export const HIGH_THRESHOLD = Number(process.env.STORAGE_HIGH_PERCENT ?? 90);

/**
 * How far past the plan allowance an account may go before uploads stop.
 *
 * A 100 MB account is refused at 110. The band is not generosity — it is what
 * stops the cap landing mid-task. A merchant part-way through listing a product
 * does not experience "you are at 100.2 MB" as a limit working correctly; they
 * experience a half-created product and a form they cannot finish. The band
 * lets them complete what they started, with the account clearly marked over
 * limit throughout, and refuses the *next* one.
 *
 * **Flat, not proportional.** Finishing the product you are in the middle of
 * costs the same handful of megabytes whether the plan is 100 MB or 10 GB, so
 * scaling the grace with the plan gives the largest accounts a gigabyte of free
 * overrun to serve a need that never exceeds a few photos.
 *
 * Above the cap the account is over its allowance and the UI says so in red;
 * above the block point nothing new is accepted at all.
 */
export const GRACE_BYTES = Number(process.env.STORAGE_GRACE_BYTES ?? 10 * 1024 * 1024);

/** The byte count at which uploads stop, for a given plan allowance. */
export function hardBlockBytes(capBytes: number): number {
  return capBytes + GRACE_BYTES;
}

/**
 * Storage quota, aware of which backend the org is writing to.
 *
 * Replaces the ProductImage-only aggregation in ImageQuotaService: usage is
 * now the sum of every registered asset (product images, logos, theme assets,
 * review photos, documents), read from the rolling total on the organization
 * rather than aggregated per upload.
 *
 * The rule that matters: **quota is charged for platform-managed bytes only.**
 * A merchant on their own bucket pays their provider directly, so blocking
 * their upload at our free-tier cap would be charging them twice — once in
 * money and once in restriction. BYOS usage is still tracked and displayed.
 */
@Injectable()
export class StorageQuotaService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly resolver: StorageAdapterResolver
  ) {}

  async getUsage(organizationId: string): Promise<StorageUsage> {
    const org = await this.prisma.organization.findUniqueOrThrow({
      where: { id: organizationId },
      select: {
        plan: true,
        storageCapBytes: true,
        storageUsedBytes: true,
        storageAssetCount: true,
        planVerifiedAt: true,
      },
    });

    const active = await this.resolver.activeConfig(organizationId);
    const quotaEnforced = active ? countsAgainstQuota(active.mode) : true;

    const trashed = await this.prisma.mediaAsset.aggregate({
      where: { organizationId, status: 'trashed' },
      _sum: { sizeBytes: true },
    });

    const planTier = resolvePlanTier(org.plan);
    const planVerified = org.planVerifiedAt != null;

    // An explicit admin override wins: a human who set a number has already
    // decided, and verification is a stand-in for exactly that decision.
    const capBytes =
      org.storageCapBytes != null
        ? Number(org.storageCapBytes)
        : effectivePlanQuotaBytes(org.plan, planVerified);
    const usedBytes = Number(org.storageUsedBytes);
    const usagePercent = capBytes > 0 ? (usedBytes / capBytes) * 100 : 0;

    return {
      usedBytes,
      capBytes,
      availableBytes: Math.max(0, capBytes - usedBytes),
      usagePercent: Number(usagePercent.toFixed(1)),
      assetCount: org.storageAssetCount,
      planTier,
      quotaEnforced,
      // A zero cap is an explicit platform-admin override (suspension for
      // non-payment or abuse), never a plan default — every tier ships a
      // real allowance.
      // A zero cap is a platform-admin suspension; anything past the block
      // point is the plan allowance running out. Both stop uploads.
      isHardBlocked: quotaEnforced && (capBytes <= 0 || usedBytes >= hardBlockBytes(capBytes)),
      warningLevel: warningLevelFor(usagePercent),
      isOverLimit: quotaEnforced && capBytes > 0 && usedBytes >= capBytes,
      blockAtBytes: capBytes > 0 ? hardBlockBytes(capBytes) : 0,
      planVerified,
      // True only when verification is what is holding the allowance down, so
      // the UI can offer "get verified" instead of "upgrade your plan" — the
      // account is already on the plan it wants.
      awaitingPlanVerification:
        planTier === 'premium' && !planVerified && org.storageCapBytes == null,
      trashedBytes: Number(trashed._sum.sizeBytes ?? 0),
    };
  }

  /**
   * Called before any byte is written or any upload URL is issued.
   *
   * Three outcomes, not two. Under the allowance it passes; over the allowance
   * but under the block point it passes *with a warning*, so a merchant part-way
   * through a task can finish it; past the block point it refuses. A zero cap is
   * a platform suspension and refuses regardless.
   *
   * When the merchant has divided their pool between stores, the store's own
   * share is checked too — and refused with the store named, because "storage
   * full" on an account showing 60% used is otherwise baffling.
   */
  async assertCanUpload(
    organizationId: string,
    incomingBytes: number,
    storeId?: string | null
  ): Promise<{ warning: boolean; usage: StorageUsage }> {
    const usage = await this.getUsage(organizationId);

    if (!usage.quotaEnforced) return { warning: false, usage };

    // An explicit platform-admin suspension, which no amount of deleting fixes.
    if (usage.capBytes <= 0) {
      throw new ForbiddenException(
        'Media storage is disabled for this account. Contact support to increase your quota.'
      );
    }

    if (usage.usedBytes >= usage.blockAtBytes) {
      // Naming the trash matters here: telling someone to "delete unused
      // media" when most of their quota is already deleted media is advice
      // they have followed and that did nothing.
      const trashHint = usage.trashedBytes > 0
        ? ` ${formatBytes(usage.trashedBytes)} of that is deleted files awaiting purge — ` +
          'empty the trash in Settings → Storage to free it now.'
        : ' Delete unused media or upgrade the plan to upload again.';

      throw new ForbiddenException(
        `Storage limit exceeded — uploads are blocked. This account is using ` +
          `${formatBytes(usage.usedBytes)} of its ${formatBytes(usage.capBytes)} ` +
          `${usage.planTier} plan allowance.${trashHint}`
      );
    }

    // Past the allowance but inside the grace band: the upload goes through so
    // the merchant can finish what they are doing, and every surface says the
    // account is over its limit.
    if (usage.usedBytes >= usage.capBytes) {
      return { warning: true, usage };
    }

    if (storeId) await this.assertStoreShare(organizationId, storeId, incomingBytes, usage.capBytes);

    return { warning: usage.usedBytes + incomingBytes > usage.capBytes, usage };
  }

  /** Enforces one store's slice of the merchant's pool. No-op in shared mode. */
  private async assertStoreShare(
    organizationId: string,
    storeId: string,
    incomingBytes: number,
    orgCapBytes: number
  ): Promise<void> {
    const org = await this.prisma.organization.findUniqueOrThrow({
      where: { id: organizationId },
      select: { storageAllocationMode: true },
    });
    const mode = org.storageAllocationMode as AllocationMode;
    if (mode === 'shared') return;

    const store = await this.prisma.store.findUnique({
      where: { id: storeId },
      select: {
        name: true,
        organizationId: true,
        storageQuotaBytes: true,
        storageQuotaPercent: true,
        storageUsedBytes: true,
      },
    });
    // A store from another organization is not this caller's to fill; the
    // upload path already asserts tenancy, so this only guards a stale id.
    if (!store || store.organizationId !== organizationId) return;

    const capBytes =
      mode === 'fixed'
        ? Number(store.storageQuotaBytes ?? 0)
        : Math.floor((orgCapBytes * (store.storageQuotaPercent ?? 0)) / 100);

    const usedBytes = Number(store.storageUsedBytes);
    if (usedBytes + incomingBytes <= capBytes) return;

    throw new ForbiddenException(
      capBytes === 0
        ? `${store.name} has no storage allocated to it. Give it a share of your plan storage in Settings → Storage.`
        : `${store.name} has used ${formatBytes(usedBytes)} of its ${formatBytes(capBytes)} share. ` +
          'Increase its share in Settings → Storage, or free up space in that store.'
    );
  }

  /** The merchant-facing view of the split, for the storage page. */
  async getAllocation(organizationId: string): Promise<AllocationSummary> {
    const org = await this.prisma.organization.findUniqueOrThrow({
      where: { id: organizationId },
      select: { plan: true, storageCapBytes: true, storageUsedBytes: true, storageAllocationMode: true, planVerifiedAt: true },
    });
    const stores = await this.prisma.store.findMany({
      where: { organizationId, deletedAt: null },
      select: {
        id: true,
        name: true,
        storageQuotaBytes: true,
        storageQuotaPercent: true,
        storageUsedBytes: true,
        storageAssetCount: true,
      },
      orderBy: { name: 'asc' },
    });

    const capBytes =
      org.storageCapBytes != null
        ? Number(org.storageCapBytes)
        : effectivePlanQuotaBytes(org.plan, org.planVerifiedAt != null);

    return resolveAllocations(
      org.storageAllocationMode as AllocationMode,
      capBytes,
      Number(org.storageUsedBytes),
      stores.map(s => ({
        storeId: s.id,
        name: s.name,
        quotaBytes: s.storageQuotaBytes == null ? null : Number(s.storageQuotaBytes),
        quotaPercent: s.storageQuotaPercent,
        usedBytes: Number(s.storageUsedBytes),
        assetCount: s.storageAssetCount,
      }))
    );
  }

  /**
   * Saves the merchant's split. Platform admins have no route into this: they
   * sell storage to the organization, and how it is divided is the merchant's
   * decision, not a support lever.
   */
  async setAllocation(
    organizationId: string,
    mode: AllocationMode,
    allocations: Array<{ storeId: string; quotaBytes?: number | null; quotaPercent?: number | null }>,
    actorId: string
  ): Promise<AllocationSummary> {
    if (!ALLOCATION_MODES.includes(mode)) {
      throw new BadRequestException(`Unknown allocation mode "${mode}"`);
    }

    const org = await this.prisma.organization.findUniqueOrThrow({
      where: { id: organizationId },
      select: { plan: true, storageCapBytes: true, planVerifiedAt: true },
    });
    const capBytes =
      org.storageCapBytes != null
        ? Number(org.storageCapBytes)
        : effectivePlanQuotaBytes(org.plan, org.planVerifiedAt != null);

    const stores = await this.prisma.store.findMany({
      where: { organizationId, deletedAt: null },
      select: { id: true, storageUsedBytes: true },
    });
    const byId = new Map(stores.map(s => [s.id, Number(s.storageUsedBytes)]));

    for (const allocation of allocations) {
      if (!byId.has(allocation.storeId)) {
        // Same shape as any other cross-tenant refusal: the caller learns
        // nothing about whether the store exists elsewhere.
        throw new BadRequestException('One of those stores does not belong to this account');
      }
    }

    const error = validateAllocation(
      mode,
      capBytes,
      allocations.map(a => ({
        storeId: a.storeId,
        quotaBytes: a.quotaBytes ?? null,
        quotaPercent: a.quotaPercent ?? null,
        usedBytes: byId.get(a.storeId) ?? 0,
      }))
    );
    if (error) throw new BadRequestException(error.message);

    await this.prisma.$transaction(async tx => {
      await tx.organization.update({
        where: { id: organizationId },
        data: { storageAllocationMode: mode, updatedBy: actorId },
      });

      for (const store of stores) {
        const allocation = allocations.find(a => a.storeId === store.id);
        await tx.store.update({
          where: { id: store.id },
          data: {
            // Switching mode clears the other mode's numbers rather than
            // leaving them to reappear later with stale values.
            storageQuotaBytes:
              mode === 'fixed' && allocation?.quotaBytes != null ? BigInt(allocation.quotaBytes) : null,
            storageQuotaPercent: mode === 'percent' ? (allocation?.quotaPercent ?? 0) : null,
            updatedBy: actorId,
          },
        });
      }
    });

    return this.getAllocation(organizationId);
  }

  /**
   * Recomputes the rolling totals from the registry.
   *
   * The counters are maintained by atomic increments, so this should be a
   * no-op — it exists because "should be" is not "is" after a partial
   * failure, and a platform admin needs a way to repair one org without a
   * database console.
   */
  async recompute(organizationId: string): Promise<{ before: number; after: number }> {
    const org = await this.prisma.organization.findUniqueOrThrow({
      where: { id: organizationId },
      select: { storageUsedBytes: true },
    });

    const configs = await this.prisma.merchantStorageConfig.findMany({
      where: { organizationId },
      select: { id: true, mode: true },
    });
    const quotaConfigIds = configs.filter(c => countsAgainstQuota(c.mode)).map(c => c.id);

    // Trashed objects still occupy real bytes until the purge cron removes
    // them, so they count — the same rule the per-config counters follow.
    const agg = await this.prisma.mediaAsset.aggregate({
      where: {
        organizationId,
        status: { in: COUNTED_STATUSES },
        storageConfigId: { in: quotaConfigIds.length ? quotaConfigIds : ['__none__'] },
      },
      _sum: { sizeBytes: true },
      _count: { _all: true },
    });

    const after = Number(agg._sum.sizeBytes ?? 0);
    await this.prisma.organization.update({
      where: { id: organizationId },
      data: {
        storageUsedBytes: BigInt(after),
        storageAssetCount: agg._count._all,
        storageUsedAt: new Date(),
      },
    });

    for (const config of configs) {
      const configAgg = await this.prisma.mediaAsset.aggregate({
        where: { storageConfigId: config.id, status: { in: COUNTED_STATUSES } },
        _sum: { sizeBytes: true },
        _count: { _all: true },
      });
      await this.prisma.merchantStorageConfig.update({
        where: { id: config.id },
        data: {
          assetBytes: BigInt(Number(configAgg._sum.sizeBytes ?? 0)),
          assetCount: configAgg._count._all,
        },
      });
    }

    // Per-store counters are repaired in the same pass. A store whose bar has
    // drifted is what a merchant notices first, since they read it far more
    // often than the account total.
    const stores = await this.prisma.store.findMany({
      where: { organizationId },
      select: { id: true },
    });
    for (const store of stores) {
      const storeAgg = await this.prisma.mediaAsset.aggregate({
        where: {
          storeId: store.id,
          status: { in: COUNTED_STATUSES },
          storageConfigId: { in: quotaConfigIds.length ? quotaConfigIds : ['__none__'] },
        },
        _sum: { sizeBytes: true },
        _count: { _all: true },
      });
      await this.prisma.store.update({
        where: { id: store.id },
        data: {
          storageUsedBytes: BigInt(Number(storeAgg._sum.sizeBytes ?? 0)),
          storageAssetCount: storeAgg._count._all,
        },
      });
    }

    return { before: Number(org.storageUsedBytes), after };
  }
}

export function warningLevelFor(percent: number): StorageUsage['warningLevel'] {
  if (percent >= 100) return 'exceeded';
  if (percent >= HIGH_THRESHOLD) return 'high';
  if (percent >= WARNING_THRESHOLD) return 'warning';
  return 'none';
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}
